import type { FonteDaFoto } from "@foto/shared";
import { versaoInicialDoVisor, type VersaoDoVisor } from "../../components/visor.js";
import { fonteParaColocar, type FotoNaBandeja } from "./folhas.js";

/**
 * O que a grade de fotos e o visor decidem sem tocar o DOM: quantas colunas, qual imagem em qual
 * versão, que nome a lupa leva. É o que a Impressão e os Marcadores têm em comum na hora de escolher
 * e conferir uma foto, separado do componente para poder ser testado.
 */

export const ROTULO_DA_PECA = "Peça com capa";
export const ROTULO_DA_FOTO_DA_IA = "Foto da IA";
export const ROTULO_DA_ORIGINAL = "Foto original";

/**
 * A largura mínima de uma coluna da grade. A coluna cresce até caber o que sobra (`1fr`), então o
 * número de colunas vem da largura da tela: o suficiente para 2 num celular de 390 px e de 4 a 6
 * numa tela larga.
 *
 * - `escolher`: a tela de escolha da foto dos Marcadores, onde a foto é o assunto.
 * - `bandeja`: a bandeja da Impressão, que divide a página com a folha e onde se colocam várias
 *   fotos em seguida.
 */
export const MINIMO_DA_GRADE = { escolher: "9rem", bandeja: "8rem" } as const;

/**
 * A largura da coluna da tela de escolha em telas largas. Sem teto, numa tela de 1920 px a grade
 * daria 12 colunas de miniatura e o olho teria de varrer a largura toda; com ele, de 4 a 6.
 */
export const LARGURA_DA_ESCOLHA = "64rem";

/** A regra CSS da grade: tantas colunas quantas couberem, nenhuma abaixo do mínimo. */
export function estiloDaGrade(minimo: string): { gridTemplateColumns: string } {
  return { gridTemplateColumns: `repeat(auto-fill, minmax(${minimo}, 1fr))` };
}

/**
 * Quantas colunas essa regra dá numa largura: a conta do `auto-fill`. O navegador a faz sozinho; aqui
 * ela existe para que o desenho — "2 colunas no celular, 4 a 6 no desktop" — fique escrito num teste e
 * não se perca quando alguém mexer no mínimo ou no espaçamento.
 */
export function colunasDaGrade(larguraPx: number, minimoPx: number, espacoPx = 8): number {
  if (larguraPx <= 0 || minimoPx <= 0) return 1;
  return Math.max(1, Math.floor((larguraPx + espacoPx) / (minimoPx + espacoPx)));
}

/** As imagens de uma foto na versão pedida: a foto da IA ou a original só quando a foto a tem. */
export function imagensNaVersao(
  foto: FotoNaBandeja,
  versao: FonteDaFoto,
): { mini: string; tela: string } {
  if (versao === "foto" && foto.foto) return foto.foto;
  if (versao === "original" && foto.original) return foto.original;
  return foto;
}

/**
 * Quais versões a tela oferece. A Impressão oferece a original (`comOriginal`); os Marcadores, não —
 * ela não serve de referência ao marcador (#94).
 */
export interface OpcoesDeVersao {
  comOriginal?: boolean;
}

/**
 * As versões que o visor alterna: a peça, e a foto da IA e a original quando a foto as tem à parte. Com
 * uma só (a foto sem capa nos Marcadores, ou a salva sem IA), a peça já é a foto, sem a chave.
 *
 * Os `id` são os de `FonteDaFoto`: o que o visor devolve ao usar já é a versão que a página guarda.
 */
export function versoesDaFoto(foto: FotoNaBandeja, opcoes: OpcoesDeVersao = {}): VersaoDoVisor[] {
  const original = opcoes.comOriginal ? foto.original : undefined;
  const peca: VersaoDoVisor = {
    id: "peca",
    // "Peça com capa" só quando há capa (a foto da IA à parte). Sem capa, a peça é a própria imagem da IA: ao
    // lado da original, ela se chama assim; sozinha, é só "Foto".
    rotulo: foto.foto ? ROTULO_DA_PECA : original ? ROTULO_DA_FOTO_DA_IA : "Foto",
    url: foto.tela,
    previa: foto.mini,
  };
  return [
    peca,
    ...(foto.foto
      ? [{ id: "foto", rotulo: ROTULO_DA_FOTO_DA_IA, url: foto.foto.tela, previa: foto.foto.mini }]
      : []),
    ...(original
      ? [{ id: "original", rotulo: ROTULO_DA_ORIGINAL, url: original.tela, previa: original.mini }]
      : []),
  ];
}

/** A versão com que o visor abre: a que a grade mostra, desde que a foto a tenha. */
export function versaoInicialDaFoto(
  foto: FotoNaBandeja,
  daGrade: FonteDaFoto,
  opcoes: OpcoesDeVersao = {},
): FonteDaFoto {
  return versaoInicialDoVisor(versoesDaFoto(foto, opcoes), daGrade) as FonteDaFoto;
}

/** O id de uma versão do visor como a página o guarda. */
export const fonteDoVisor = (id: string): FonteDaFoto =>
  id === "foto" || id === "original" ? id : "peca";

/** A versão que vale para a foto quando a página pede `desejada`: a mesma regra de "colocar na folha". */
export function versaoEmUso(foto: FotoNaBandeja | undefined, desejada: FonteDaFoto): FonteDaFoto {
  return fonteParaColocar(foto, desejada) ?? "peca";
}

/** O nome da lupa: ela amplia uma foto de alguém, e o leitor de tela precisa dizer de quem. */
export const rotuloDaLupa = (nome: string): string => `Ampliar a foto de ${nome}`;

/** O que o toque na miniatura faz na tela de escolha dos Marcadores, dito para o leitor de tela. */
export const rotuloDeEscolher = (nome: string): string => `Escolher a foto de ${nome}`;

/**
 * O que o toque na miniatura faz na Impressão. A etiqueta de uso ("2×") é visual; quem não a vê
 * ouve o mesmo aqui.
 */
export function rotuloDeColocar(nome: string, usos = 0): string {
  const base = `Colocar a foto de ${nome} na folha`;
  if (usos <= 0) return base;
  return `${base}, já em ${usos} ${usos === 1 ? "espaço" : "espaços"}`;
}

/** O título e o alt do visor de uma foto. */
export const tituloDaFoto = (nome: string): string => `Foto de ${nome}`;

/** A legenda da versão que está à vista, para a chave de versões da tela de escolha. */
export function legendaDaVersao(
  versao: FonteDaFoto,
  legendaPeca: string,
  legendaFoto: string,
  legendaOriginal: string = legendaPeca,
): string {
  if (versao === "foto") return legendaFoto;
  if (versao === "original") return legendaOriginal;
  return legendaPeca;
}
